import { useState, useCallback } from "react";
import { reviewStore, UserReview } from "@/utils/reviewStore";
import ReviewWriteModal from "@/pages/detail/components/ReviewWriteModal";

interface Props {
  accommodationId: number;
  accommodationName: string;
  rating: number;
  reviewCount: number;
}

const STATIC_REVIEWS = [
  { id: "s1", nickname: "김수연", date: "2025.03", text: "호스트분이 정말 친절하고 카카오톡으로 모든 질문에 빠르게 답해주셔서 너무 편했어요. 위치도 완벽하고 사진보다 실제가 훨씬 예쁩니다!", rating: 5, reply: undefined },
  { id: "s2", nickname: "이민준", date: "2025.02", text: "일본어 하나도 못하는 부모님 모시고 갔는데 한국어로 소통 완전 가능해서 너무 좋았어요. 체크인부터 체크아웃까지 불편함 없이 완벽했습니다.", rating: 5, reply: undefined },
  { id: "s3", nickname: "박지영", date: "2025.01", text: "숙소가 청결하고 사진과 일치해요. 맛집 추천도 해주셔서 덕분에 아주 맛있는 라멘 먹었습니다. 다음에 후쿠오카 오면 또 여기 예약할 것 같아요.", rating: 5, reply: undefined },
  { id: "s4", nickname: "최준혁", date: "2024.12", text: "커플 여행으로 왔는데 로맨틱한 분위기가 최고였어요! 호스트분이 웰컴 드링크도 준비해두셔서 감동받았습니다. 강력 추천합니다.", rating: 5, reply: undefined },
  { id: "s5", nickname: "강하나", date: "2024.11", text: "혼자 여행자인데 호스트분이 동네 맛집 지도까지 만들어서 주셨어요. 덕분에 숨은 로컬 식당도 많이 발견했습니다. 편의 시설도 모두 완비되어 있어요.", rating: 4, reply: undefined },
];

interface DisplayReview {
  id: string;
  nickname: string;
  date: string;
  text: string;
  rating: number;
  reply?: string;
}

export default function DetailReviews({ accommodationId, accommodationName, rating, reviewCount }: Props) {
  const [showModal, setShowModal] = useState(false);
  const [userReviews, setUserReviews] = useState<UserReview[]>(() =>
    reviewStore.getApproved(accommodationId)
  );

  const refreshUserReviews = useCallback(() => {
    setUserReviews(reviewStore.getApproved(accommodationId));
  }, [accommodationId]);

  const allReviews: DisplayReview[] = [
    ...STATIC_REVIEWS,
    ...userReviews.map((r) => ({
      id: r.id,
      nickname: r.nickname,
      date: r.date,
      text: r.text,
      rating: r.rating,
      reply: r.reply,
    })),
  ];

  const totalCount = reviewCount + userReviews.length;

  return (
    <section className="mt-10 pt-8 border-t border-stone-100">
      {showModal && (
        <ReviewWriteModal
          accommodationId={accommodationId}
          accommodationName={accommodationName}
          onClose={() => setShowModal(false)}
          onSubmitted={refreshUserReviews}
        />
      )}

      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <h2 className="text-lg sm:text-xl font-bold text-stone-900 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-star-fill text-amber-400 text-lg"></i>
          </div>
          이용 후기
          <span className="text-base font-normal text-stone-400 ml-1">({totalCount}개)</span>
        </h2>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-amber-50 px-3 sm:px-4 py-2 rounded-full">
            <i className="ri-star-fill text-amber-400 text-sm"></i>
            <span className="font-bold text-stone-800">{rating}</span>
            <span className="text-stone-400 text-sm">/ 5.0</span>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap transition-colors"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-edit-line text-sm"></i>
            </div>
            리뷰 작성
          </button>
        </div>
      </div>

      {/* 리뷰 그리드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {allReviews.map((review) => (
          <div key={review.id} className="bg-stone-50 rounded-xl p-4 sm:p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-amber-200 rounded-full text-amber-800 font-bold text-sm shrink-0">
                  {review.nickname.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-semibold text-stone-800">{review.nickname}</div>
                  <div className="text-xs text-stone-400">{review.date}</div>
                </div>
              </div>
              <div className="flex gap-0.5 shrink-0">
                {Array.from({ length: review.rating }).map((_, s) => (
                  <i key={s} className="ri-star-fill text-amber-400 text-xs"></i>
                ))}
                {Array.from({ length: 5 - review.rating }).map((_, s) => (
                  <i key={`e${s}`} className="ri-star-line text-stone-300 text-xs"></i>
                ))}
              </div>
            </div>
            <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">{review.text}</p>

            {review.reply && (
              <div className="mt-3 bg-white rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 border-l-2 border-amber-300">
                <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                  <i className="ri-reply-line text-xs"></i>
                  호스트 답글
                </p>
                <p className="text-xs sm:text-sm text-stone-600">{review.reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 리뷰 작성 유도 배너 */}
      <div className="mt-6 bg-gradient-to-r from-amber-50 to-stone-50 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-amber-100">
        <div>
          <p className="text-sm font-semibold text-stone-800">이 숙소에서 머무셨나요?</p>
          <p className="text-xs text-stone-500 mt-0.5">소중한 후기가 다른 여행자들에게 큰 도움이 돼요.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-pencil-line text-sm"></i>
          </div>
          리뷰 남기기
        </button>
      </div>
    </section>
  );
}
